import { useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { useCustomerAuth } from "./useCustomerAuth";

const LS_KEY = "msc_wishlist_guest";

function readGuest() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch { return {}; }
}
function writeGuest(obj) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(obj || {})); } catch {}
}

/**
 * useWishlist — single source of truth côté client.
 * - Pour un customer connecté : source = backend (/api/wishlist)
 * - Pour un guest : source = localStorage
 * On expose `toggle(product_id)`, `isFavorite(id)`, `items`, `daysSinceFavorited(id)`, `count`.
 */
export function useWishlist() {
  const { customer } = useCustomerAuth();
  const [items, setItems] = useState([]);       // liste enrichie (guest: ids seuls)
  const [map, setMap] = useState({});            // product_id -> {favorited_at}
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (customer) {
      setLoading(true);
      try {
        const { data } = await api.get("/wishlist");
        const list = data.items || [];
        setItems(list);
        const m = {};
        for (const it of list) m[it.id] = { favorited_at: it.favorited_at };
        setMap(m);
      } catch {
        setItems([]);
        setMap({});
      } finally { setLoading(false); }
    } else {
      const guest = readGuest();
      setMap(guest);
      // Fetch product details lazily (leave items as simple ids-with-date)
      const ids = Object.keys(guest);
      if (ids.length === 0) { setItems([]); return; }
      setLoading(true);
      try {
        const { data } = await api.get("/shop/products", { params: { ids: ids.join(",") } });
        const prods = data || [];
        const enriched = prods.map(p => ({ ...p, favorited_at: guest[p.id]?.favorited_at }))
                              .sort((a, b) => (b.favorited_at || "").localeCompare(a.favorited_at || ""));
        setItems(enriched);
      } catch {
        setItems([]);
      } finally { setLoading(false); }
    }
  }, [customer]);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for cross-tab updates
  useEffect(() => {
    const onStorage = e => { if (e.key === LS_KEY) refresh(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refresh]);

  const isFavorite = useCallback((pid) => !!map[pid], [map]);

  const toggle = useCallback(async (pid) => {
    if (customer) {
      const { data } = await api.post("/wishlist/toggle", { product_id: pid });
      setMap(m => {
        const copy = { ...m };
        if (data.favorited) copy[pid] = { favorited_at: data.created_at };
        else delete copy[pid];
        return copy;
      });
      refresh();
      return data.favorited;
    } else {
      const g = readGuest();
      if (g[pid]) delete g[pid];
      else g[pid] = { favorited_at: new Date().toISOString() };
      writeGuest(g);
      setMap({ ...g });
      refresh();
      return !!g[pid];
    }
  }, [customer, refresh]);

  const daysSinceFavorited = useCallback((pid) => {
    const at = map[pid]?.favorited_at;
    if (!at) return null;
    const diff = (Date.now() - new Date(at).getTime()) / 86400000;
    return Math.floor(diff);
  }, [map]);

  return { items, map, count: Object.keys(map).length, isFavorite, toggle, daysSinceFavorited, loading, refresh, isGuest: !customer };
}
